import { useEffect, useRef, useState, useCallback } from "react";
import { ChevronLeft, ChevronRight, ArrowRight } from "lucide-react";

/**
 * Carousel orizzontale swipeable mobile-first con:
 *  - scroll-snap nativo CSS (performance e touch naturale)
 *  - indicatore "Scorri" animato che scompare dopo il primo interaction
 *  - dots di pagination (1 per card, attivo = card corrente)
 *  - frecce tastiera/mouse visibili ≥ md (desktop)
 *  - keyboard navigation (arrow keys quando focused)
 *
 * Props:
 *  - children: array di elementi JSX (una card per elemento)
 *  - testId: radice per i data-testid (default "swipe")
 *  - ariaLabel: label per screen reader
 *  - cardMinWidth: larghezza minima di ogni card (default 280px)
 *  - gap: spazio tra card (default 16px)
 *  - showArrowsDesktop: mostra le frecce su desktop (default true)
 *  - hintLabel: testo dell'hint "scorri" (default "Scorri")
 */
export default function SwipeableCards({
  children,
  testId = "swipe",
  ariaLabel = "Carousel",
  cardMinWidth = 280,
  gap = 16,
  showArrowsDesktop = true,
  hintLabel = "Scorri per scoprire",
}) {
  const scrollerRef = useRef(null);
  const [activeIdx, setActiveIdx] = useState(0);
  const [hintVisible, setHintVisible] = useState(true);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const items = Array.isArray(children) ? children : [children];
  const itemCount = items.length;

  const updateActive = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const { scrollLeft, clientWidth, scrollWidth } = el;
    setCanScrollLeft(scrollLeft > 8);
    setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 8);
    // Card attiva: la più vicina al centro del viewport dello scroller
    const center = scrollLeft + clientWidth / 2;
    let closestIdx = 0;
    let closestDist = Infinity;
    el.querySelectorAll("[data-swipe-card]").forEach((card, idx) => {
      const cardCenter = card.offsetLeft + card.offsetWidth / 2;
      const dist = Math.abs(cardCenter - center);
      if (dist < closestDist) {
        closestDist = dist;
        closestIdx = idx;
      }
    });
    setActiveIdx(closestIdx);
  }, []);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    updateActive();
    const onScroll = () => {
      updateActive();
      setHintVisible(false);
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    const onResize = () => updateActive();
    window.addEventListener("resize", onResize);
    return () => {
      el.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onResize);
    };
  }, [updateActive, itemCount]);

  // Nascondi hint dopo 4s anche senza interaction
  useEffect(() => {
    const t = setTimeout(() => setHintVisible(false), 4500);
    return () => clearTimeout(t);
  }, []);

  const scrollToIdx = useCallback((idx) => {
    const el = scrollerRef.current;
    if (!el) return;
    const cards = el.querySelectorAll("[data-swipe-card]");
    const target = cards[Math.max(0, Math.min(idx, cards.length - 1))];
    if (target) {
      el.scrollTo({ left: target.offsetLeft - el.offsetLeft, behavior: "smooth" });
    }
    setHintVisible(false);
  }, []);

  const scrollByOne = useCallback((direction) => {
    const el = scrollerRef.current;
    if (!el) return;
    const card = el.querySelector("[data-swipe-card]");
    const step = card ? card.offsetWidth + gap : el.clientWidth * 0.8;
    el.scrollBy({ left: direction * step, behavior: "smooth" });
    setHintVisible(false);
  }, [gap]);

  const onKeyDown = (e) => {
    if (e.key === "ArrowLeft") {
      e.preventDefault();
      scrollByOne(-1);
    } else if (e.key === "ArrowRight") {
      e.preventDefault();
      scrollByOne(1);
    }
  };

  if (itemCount === 0) return null;

  return (
    <div className="relative" data-testid={`${testId}-root`}>
      {/* Hint animato "Scorri" — solo mobile, sparisce al primo scroll */}
      {hintVisible && itemCount > 1 && (
        <div
          data-testid={`${testId}-hint`}
          className="md:hidden absolute top-0 right-4 -mt-8 z-10 pointer-events-none flex items-center gap-1.5 text-[11px] font-medium text-[#4A5E51] bg-white/90 backdrop-blur px-2.5 py-1 rounded-full shadow-sm animate-pulse"
        >
          <span>{hintLabel}</span>
          <ArrowRight className="w-3 h-3 animate-bounce-horizontal" />
        </div>
      )}

      {/* Scroller orizzontale con snap */}
      <div
        ref={scrollerRef}
        onKeyDown={onKeyDown}
        role="region"
        aria-label={ariaLabel}
        tabIndex={0}
        data-testid={`${testId}-scroller`}
        className="flex overflow-x-auto overflow-y-hidden snap-x snap-mandatory scrollbar-hide scroll-smooth -mx-4 px-4 md:-mx-2 md:px-2 focus:outline-none"
        style={{ gap: `${gap}px`, scrollPaddingLeft: "16px", scrollPaddingRight: "16px" }}
      >
        {items.map((child, idx) => (
          <div
            key={idx}
            data-swipe-card
            data-testid={`${testId}-card-${idx}`}
            className="snap-start shrink-0"
            style={{ minWidth: `${cardMinWidth}px`, maxWidth: "92vw" }}
          >
            {child}
          </div>
        ))}
        {/* Spacer coda per far terminare ultimo card snap a sinistra */}
        <div className="shrink-0 w-4 md:w-2" aria-hidden />
      </div>

      {/* Dots pagination — visibili solo se > 1 card */}
      {itemCount > 1 && (
        <div
          className="flex items-center justify-center gap-1.5 mt-4"
          role="tablist"
          aria-label="Naviga tra le card"
          data-testid={`${testId}-dots`}
        >
          {items.map((_, idx) => (
            <button
              key={idx}
              type="button"
              role="tab"
              aria-selected={idx === activeIdx}
              aria-label={`Vai alla card ${idx + 1}`}
              onClick={() => scrollToIdx(idx)}
              data-testid={`${testId}-dot-${idx}`}
              className={`transition-all duration-300 rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-[#163321]/40 ${
                idx === activeIdx
                  ? "w-6 h-2 bg-[#163321]"
                  : "w-2 h-2 bg-[#163321]/25 hover:bg-[#163321]/50"
              }`}
            />
          ))}
        </div>
      )}

      {/* Frecce desktop — nascoste su mobile */}
      {showArrowsDesktop && itemCount > 1 && (
        <>
          <button
            type="button"
            onClick={() => scrollByOne(-1)}
            disabled={!canScrollLeft}
            aria-label="Card precedente"
            data-testid={`${testId}-prev`}
            className="hidden md:flex items-center justify-center absolute left-0 top-1/2 -translate-y-1/2 -translate-x-1/2 w-10 h-10 rounded-full bg-white shadow-md border border-zinc-200 text-[#163321] hover:bg-zinc-50 disabled:opacity-30 disabled:cursor-not-allowed transition z-10"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button
            type="button"
            onClick={() => scrollByOne(1)}
            disabled={!canScrollRight}
            aria-label="Card successiva"
            data-testid={`${testId}-next`}
            className="hidden md:flex items-center justify-center absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2 w-10 h-10 rounded-full bg-white shadow-md border border-zinc-200 text-[#163321] hover:bg-zinc-50 disabled:opacity-30 disabled:cursor-not-allowed transition z-10"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </>
      )}
    </div>
  );
}
